/* Saige Gift — live Instagram feed --------------------------------------- */
/*
 * Liquid cannot call an external API, and Instagram's own API needs a token
 * that expires every 60 days, so the feed is fetched in the browser from
 * Behold (behold.so). Behold holds and refreshes the Instagram token and
 * republishes the account's posts as plain JSON at a public feed URL — no
 * credential ships in the theme.
 *
 * Feed shape (Behold v2):
 *   { username, posts: [ { permalink, mediaType, mediaUrl, thumbnailUrl,
 *     sizes: { small|medium|large|full: { mediaUrl, width, height } },
 *     altText, prunedCaption } ] }
 *
 * If the feed is unreachable, empty or malformed the grid is left untouched,
 * so the images set in the customizer stay on the page as the fallback.
 */
(function () {
  'use strict';

  var CACHE_PREFIX = 'saige:ig:';
  var CACHE_TTL = 30 * 60 * 1000;

  function truncate(text, max) {
    if (!text) return '';
    text = String(text).replace(/\s+/g, ' ').trim();
    return text.length > max ? text.slice(0, max - 1) + '…' : text;
  }

  /* A VIDEO post's mediaUrl is the video file itself, so a reel has to fall
     back to its poster frame before anything else. */
  function pickImage(post) {
    var sizes = post.sizes || {};
    if (post.mediaType === 'VIDEO' && post.thumbnailUrl) {
      return { mediaUrl: post.thumbnailUrl };
    }
    var size = sizes.medium || sizes.large || sizes.small || sizes.full;
    if (size && size.mediaUrl) return size;
    if (post.thumbnailUrl) return { mediaUrl: post.thumbnailUrl };
    if (post.mediaUrl) return { mediaUrl: post.mediaUrl };
    return null;
  }

  /* Keep only the fields the grid renders — the raw feed carries captions,
     hashtags and comment counts that would bloat the session cache. */
  function trimPosts(posts, count) {
    var out = [];
    for (var i = 0; i < posts.length && out.length < count; i++) {
      var post = posts[i] || {};
      var image = pickImage(post);
      if (!image || !image.mediaUrl) continue;
      out.push({
        permalink: post.permalink || '',
        mediaUrl: image.mediaUrl,
        width: image.width || 0,
        height: image.height || 0,
        alt: post.altText || truncate(post.prunedCaption || post.caption, 120)
      });
    }
    return out;
  }

  function buildItem(post, fallbackLink) {
    var link = document.createElement('a');
    link.className = 'socials__item';
    link.href = post.permalink || fallbackLink || '#';
    link.target = '_blank';
    link.rel = 'noopener';

    var img = document.createElement('img');
    img.src = post.mediaUrl;
    img.alt = post.alt || 'View this post on Instagram';
    img.loading = 'lazy';
    if (post.width) img.width = post.width;
    if (post.height) img.height = post.height;

    link.appendChild(img);
    return link;
  }

  function render(grid, posts) {
    if (!posts.length) return;
    var fallbackLink = grid.getAttribute('data-instagram-link');
    var fragment = document.createDocumentFragment();
    for (var i = 0; i < posts.length; i++) {
      fragment.appendChild(buildItem(posts[i], fallbackLink));
    }
    grid.innerHTML = '';
    grid.appendChild(fragment);
    grid.setAttribute('data-instagram-live', 'true');
  }

  /* sessionStorage throws outright in some privacy modes, so every access is
     guarded and a miss just means one more request. */
  function readCache(key) {
    try {
      var raw = sessionStorage.getItem(CACHE_PREFIX + key);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!entry || Date.now() - entry.time > CACHE_TTL) return null;
      return entry.posts;
    } catch (e) {
      return null;
    }
  }

  function writeCache(key, posts) {
    try {
      sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ time: Date.now(), posts: posts }));
    } catch (e) {
      /* quota or private mode — the feed still works, it just re-fetches */
    }
  }

  function load(grid) {
    var url = grid.getAttribute('data-instagram-feed');
    if (!url || grid.getAttribute('data-instagram-live') === 'true') return;

    var count = parseInt(grid.getAttribute('data-instagram-count'), 10) || 4;
    var cacheKey = url + '|' + count;

    var cached = readCache(cacheKey);
    if (cached) {
      render(grid, cached);
      return;
    }

    fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('feed responded ' + response.status);
        return response.json();
      })
      .then(function (data) {
        /* v2 feeds nest posts under `posts`; v1 returned a bare array. */
        var raw = (data && data.posts) || (Array.isArray(data) ? data : []);
        var posts = trimPosts(raw, count);
        if (!posts.length) throw new Error('feed contained no usable posts');
        writeCache(cacheKey, posts);
        render(grid, posts);
      })
      .catch(function (error) {
        if (window.console) {
          console.warn('[Saige] Instagram feed unavailable, showing fallback images:', error.message);
        }
      });
  }

  function init() {
    var grids = document.querySelectorAll('[data-instagram-feed]');
    for (var i = 0; i < grids.length; i++) load(grids[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* The theme editor re-renders the section on every setting change. */
  document.addEventListener('shopify:section:load', init);
})();
